import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { getCronofyEvents } from 'app/shared-components/util/cronofyApi';
import {startLoading, stopLoading} from "app/store/utilSlice";
import { fetchMergedCalendarData } from 'app/shared-components/scheduling/calendarDataService';
import { deleteMeeting, fetchMeeting, fetchMeetingByEventId } from './meetingsSlice';

/**
 * Calendar Events Slice
 * 
 * Manages calendar events with efficient time-based storage and querying.
 * Handles both managed events (created through system) and external events.
 * 
 * Features:
 * - Time-ordered storage for efficient range queries
 * - Caching to avoid redundant API calls
 * - Managed vs external event differentiation
 * - Session event detection via foreignModelKey
 * - Optimistic loading for better UX
 */

// ===== HELPER FUNCTIONS =====

/**
 * Extracts tags from Cronofy event response
 */
function extractTagsFromCronofyEvent(event) {
  if (!event.tags || !event.tags.private) return [];
  return event.tags.private.map(tag => tag.value).filter(Boolean);
}

/**
 * Detects if an event is a ClientSession based on tags
 * Sessions have a tag with pattern "ClientService:{id}"
 */
function isSessionEvent(event) {
  if (!event?.tags || !Array.isArray(event.tags)) return false;
  return event.tags.some(tag => tag?.startsWith('ClientService:'));
}

/**
 * Extracts session ID from tags
 * Returns null if not a session event
 */
export function getSessionIdFromTags(tags) {
  if (!tags || !Array.isArray(tags)) return null;
  const sessionTag = tags.find(tag => tag?.startsWith('ClientService:'));
  return sessionTag ? sessionTag.split(':')[1] : null;
}



// ===== ASYNC THUNKS =====

/**
 * Fetches events for a date range - now includes merged meeting data
 */
export const fetchCalendarEvents = createAsyncThunk(
  'calendarEvents/fetchEvents',
  async ({ from, to }, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(startLoading());

      const state = getState();
      const userId = state.user?.id;
      
      if (!userId) {
        throw new Error('User ID not available');
      }

      // Fetch merged calendar data (events + booked meetings)
      const mergedData = await fetchMergedCalendarData(userId, from, to);
      
      // Transform merged data to event format
      const events = mergedData.map(item => {
        const startDate = new Date(item.start);
        const endDate = new Date(item.end);
        
        return {
          id: item.eventId || item.meetingId,
          cronofyId: item.cronofyId || item.eventId,
          title: item.title || item.summary || 'Untitled Event',
          description: item.description || '',
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          startTime: startDate.getTime(),
          location: typeof item.location === 'string' ? item.location : (item.location?.description || ''),
          calendarId: item.calendarId,
          attendees: item.attendees || [],
          organizer: item.organizer,
          conferencing: item.conferencing,
          isManaged: item.type === 'meeting' || item.resource?.isManaged,
          eventId: item.eventId,
          tags: item.tags || [],
          isSession: item.resource?.isSession || false,
          // Meeting-specific fields
          isMeeting: item.type === 'meeting',
          meetingId: item.meetingId,
          meetingRole: item.role, // 'organizer' or 'attendee'
          meetingIcon: item.icon,
          meetingLabel: item.label,
          syncedToCalendar: item.syncedToCalendar,
          organizerUserId: item.organizerUserId,
          attendeeUserIds: item.attendeeUserIds,
          meetingTypeId: item.meetingTypeId,
          originalEvent: item.originalEvent || item
        };
      });
      
      return {
        events,
        from,
        to,
        fetchedAt: Date.now()
      };
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      return rejectWithValue(error.message);
    } finally {
      dispatch(stopLoading());
    }
  }
);

// ===== SLICE =====

const calendarEventsSlice = createSlice({
  name: 'calendarEvents',
  initialState: {
    // Events stored in time-ordered array for efficient range queries
    events: [],
    // Track loaded date ranges to avoid redundant fetches
    loadedRanges: [],
    // Currently selected event for detail view
    selectedEvent: null,
    // Loading states
    loading: false,
    error: null,
    // Last fetch timestamp for cache invalidation
    lastFetch: null
  },
  reducers: {
    /**
     * Selects an event for detailed view
     */
    selectEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },

    /**
     * Clears selected event
     */
    clearSelectedEvent: (state) => {
      state.selectedEvent = null;
    },

    /**
     * Adds or updates a single event (for real-time updates)
     */
    upsertEvent: (state, action) => {
      const newEvent = action.payload;
      const existingIndex = state.events.findIndex(e => e.id === newEvent.id);
      
      if (existingIndex >= 0) {
        // Update existing event
        state.events[existingIndex] = newEvent;
      } else {
        // Add new event and maintain time order
        state.events.push(newEvent);
      }
      
      // Re-sort by start time to maintain order
      state.events.sort((a, b) => a.startTime - b.startTime);
    },

    /**
     * Removes an event
     */
    removeEvent: (state, action) => {
      const eventId = action.payload;
      state.events = state.events.filter(e => e.id !== eventId);
      
      // Clear selection if removed event was selected
      if (state.selectedEvent?.id === eventId) {
        state.selectedEvent = null;
      }
    },

    /**
     * Clears all events and ranges (for calendar reconnection)
     */
    clearEvents: (state) => {
      state.events = [];
      state.loadedRanges = [];
      state.selectedEvent = null;
      state.lastFetch = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        const { events, from, to, fetchedAt } = action.payload;
        
        // Remove events in the fetched range to avoid duplicates
        const fromTime = new Date(from).getTime();
        const toTime = new Date(to).getTime();
        state.events = state.events.filter(event => 
          event.startTime < fromTime || event.startTime > toTime
        );
        
        // Add new events and sort by start time
        state.events.push(...events);
        state.events.sort((a, b) => a.startTime - b.startTime);
        
        // Track loaded range
        const newRange = { from, to, fetchedAt };
        state.loadedRanges.push(newRange);
        
        // Clean up old ranges (keep last 10)
        if (state.loadedRanges.length > 10) {
          state.loadedRanges = state.loadedRanges.slice(-10);
        }
        
        state.loading = false;
        state.lastFetch = fetchedAt;
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        const meetingId = action.payload.id;
        // Remove event by meetingId (attendee meetings) or by id matching meetingId (direct match)
        state.events = state.events.filter(e => 
          e.meetingId !== meetingId && e.id !== meetingId
        );
        // Clear selection if deleted meeting was selected
        if (state.selectedEvent?.meetingId === meetingId || state.selectedEvent?.id === meetingId) {
          state.selectedEvent = null;
        }
      })
      .addCase(fetchMeeting.fulfilled, (state, action) => {
        // When meeting is fetched, also remove its calendar event if it matches managedCalendarEventId
        const meeting = action.payload;
        if (meeting?.managedCalendarEventId) {
          const eventToRemove = state.events.find(e => e.eventId === meeting.managedCalendarEventId);
          if (eventToRemove) {
            state.events = state.events.filter(e => e.eventId !== meeting.managedCalendarEventId);
          }
        }
      })
      .addCase(fetchMeetingByEventId.fulfilled, (state, action) => {
        // When meeting is fetched by eventId, store the mapping for later deletion
        const meeting = action.payload;
        if (meeting?.managedCalendarEventId) {
          // Find and update the event to include meetingId for easier deletion later
          const eventIndex = state.events.findIndex(e => e.eventId === meeting.managedCalendarEventId);
          if (eventIndex >= 0) {
            state.events[eventIndex].meetingId = meeting.id;
          }
        }
      });
  }
});

// ===== ACTIONS =====
export const { 
  selectEvent, 
  clearSelectedEvent, 
  upsertEvent, 
  removeEvent, 
  clearEvents 
} = calendarEventsSlice.actions;

// ===== SELECTORS =====

/**
 * Selects all events
 */
export const selectAllEvents = (state) => state.calendarEvents.events;

/**
 * Selects events for a specific date range (memoized)
 */
export const selectEventsByRange = createSelector(
  [(state) => state.calendarEvents?.events || [], (state, from, to) => ({ from, to })],
  (events, { from, to }) => {
    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();
    return events.filter(event => 
      event.startTime >= fromTime && event.startTime <= toTime
    );
  }
);

/**
 * Selects only managed events
 */
export const selectManagedEvents = (state) => 
  state.calendarEvents.events.filter(event => event.isManaged);

/**
 * Selects only external events
 */
export const selectExternalEvents = (state) => 
  state.calendarEvents.events.filter(event => !event.isManaged);

/**
 * Selects only session events
 */
export const selectSessionEvents = (state) => 
  state.calendarEvents.events.filter(event => event.isSession);

/**
 * Selects currently selected event
 */
export const selectSelectedEvent = (state) => state.calendarEvents.selectedEvent;

/**
 * Selects loading state
 */
export const selectEventsLoading = (state) => state.calendarEvents.loading;

/**
 * Selects error state
 */
export const selectEventsError = (state) => state.calendarEvents.error;

/**
 * Checks if a date range is already loaded
 */
export const selectIsRangeLoaded = (from, to) => (state) => {
  const fromTime = new Date(from).getTime();
  const toTime = new Date(to).getTime();
  
  return state.calendarEvents.loadedRanges.some(range => {
    const rangeFrom = new Date(range.from).getTime();
    const rangeTo = new Date(range.to).getTime();
    
    // Check if requested range is within a loaded range
    return rangeFrom <= fromTime && rangeTo >= toTime;
  });
};

/**
 * Gets events formatted for react-big-calendar (memoized)
 */
export const selectEventsForCalendar = createSelector(
  [(state, from, to) => selectEventsByRange(state, from, to)],
  (events) => events.map(event => ({
    id: event.id,
    title: event.title,
    start: new Date(event.start),
    end: new Date(event.end),
    resource: event,
    className: event.isManaged ? 'managed-event' : 'external-event'
  }))
);

export default calendarEventsSlice.reducer;